import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

export interface AccessInfoData {
  name: string;
  email: string;
}

/**
 * Pop-up "Zugang einrichten" (Club-Manager-Login-Spec 5): who sets up accounts and how to
 * reach them. Closes via ✕, "Schließen", the backdrop and Escape (MatDialog).
 */
@Component({
  selector: 'app-access-info-dialog',
  template: `
    <div class="cmc-auth-dialog">
      <header class="cmc-auth-dialog__head">
        <span class="cmc-auth-dialog__icon" aria-hidden="true"><mat-icon>waving_hand</mat-icon></span>
        <div class="min-w-0">
          <h2 class="cmc-auth-dialog__title" id="accessInfoTitle">{{ 'auth.access_title' | translate }}</h2>
          <p class="cmc-auth-dialog__subtitle">{{ 'auth.access_subtitle' | translate }}</p>
        </div>
        <button type="button" class="cmc-auth-icon-button cmc-auth-dialog__close" mat-dialog-close
          [attr.aria-label]="'auth.close' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </header>

      <p class="cmc-auth-dialog__text">{{ 'auth.access_text' | translate }}</p>

      <div class="cmc-auth-contact">
        <span class="cmc-auth-contact__avatar" aria-hidden="true">{{ initials() }}</span>
        <div class="cmc-auth-contact__body">
          @if (data.name) {
            <strong class="cmc-auth-contact__name">{{ data.name }}</strong>
          }
          <span class="cmc-auth-contact__role">{{ 'auth.webmaster' | translate }}</span>
          <a class="cmc-auth-contact__email" [href]="mailto">{{ data.email }}</a>
        </div>
        <button type="button" class="cmc-auth-button cmc-auth-button--secondary cmc-auth-button--sm"
          (click)="copy()" aria-live="polite">
          @if (copied()) {
            <mat-icon aria-hidden="true">check</mat-icon>{{ 'auth.copied' | translate }}
          } @else {
            {{ 'auth.copy' | translate }}
          }
        </button>
      </div>

      <div class="cmc-auth-dialog__actions">
        <button type="button" class="cmc-auth-button cmc-auth-button--secondary" mat-dialog-close>
          {{ 'auth.close' | translate }}
        </button>
        <a class="cmc-auth-button" [href]="mailto">
          <mat-icon aria-hidden="true">mail</mat-icon>{{ 'auth.write_email' | translate }}
        </a>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogClose, MatIcon, TranslateModule],
})
export class AccessInfoDialogComponent {
  private readonly clipboard = inject(Clipboard);
  readonly data = inject<AccessInfoData>(MAT_DIALOG_DATA);

  readonly copied = signal(false);
  readonly mailto = `mailto:${this.data.email}?subject=${encodeURIComponent('Zugang Club Manager')}`;
  readonly initials = computed(() => {
    const parts = (this.data.name || 'Webmaster').split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  });

  copy(): void {
    this.copied.set(this.clipboard.copy(this.data.email));
  }
}
